import { useCallback, useEffect, useMemo, useState } from "react"

/**
 * @template  {string} T
 * @param  {T} key
 */

export default function hookVersionStoreFactory(key = 'collection') {
  return ({ [key]: collection, fetch: baseFetch }) => {
    const { reset, fetchById, save } = collection
    const [version, setVersion] = useState(null)

    const resetVersion = useCallback(() => {
      reset()
      setVersion(null)
    }, [reset])

    useEffect(() => () => {
      resetVersion()
    }, [resetVersion])

    const addVersion = useCallback((params = {}) => {
      if (version) {
        const { id, entityId } = version
        params.version = { id, entityId }
      }
      return params
    }, [version])

    const fetch = useCallback(async ({ params, ...payload }) => await baseFetch({
      ...payload,
      params: addVersion(params),
    }), [baseFetch, addVersion])

    const fetchVersion = useCallback(async (id, params = {}) => {
      params = addVersion(params)
      return await fetchById(id, params)
    }, [fetchById, addVersion])

    const saveVersion = useCallback(async (data, params = {}) => {
      params = addVersion(params)
      return await save(data, params)
    }, [save, addVersion])

    const setVersionEntry = useCallback(async version => {
      const { id, entityId } = version
      await fetchById(
        entityId,
        { version: { id, entityId } },
      )
      setVersion(version)
    }, [fetchById])

    const resetVersionEntry = useCallback(async id => {
      setVersion(version => {
        id = id || version.entityId
        return null
      })
      await fetchById(id)
    }, [setVersion, fetchById])

    return useMemo(() => {
      const store = {
        ...collection,
        reset: resetVersion,
        fetchById: fetchVersion,
        save: saveVersion,

        version: {
          item: version,
          setItem: setVersionEntry,
          reset: resetVersionEntry,
        },
      }

      /** @type {{[x in K]: store}} */
      const result = {
        [key]: store,
        fetch,
      }
      return result
    }, [collection, fetch, version, resetVersion, fetchVersion, saveVersion, setVersionEntry, resetVersionEntry])
  }
}
